import { useId, useLayoutEffect, useRef, useState, type ChangeEvent } from 'react';
import { CURRENCY_SYMBOL, caretForDigitIndex, digitsBeforeCaret, formatStoredAmount } from '@/lib/currency';
import { useTranslations } from '@/i18n/use-translations';
import {
  DECIMAL_SEPARATOR,
  caretAfterDecimalSeparator,
  formatDecimalInput,
  parseDecimalInput,
} from '@/lib/number-format';

type CurrencyInputProps = {
  readonly id?: string;
  /** Clean numeric amount, or `null` for an empty field. Never `0` for "no value". */
  readonly value: number | null;
  readonly placeholder?: string;
  readonly onChange: (value: number | null) => void;
  /** `0` for whole-Euro amounts, `2` for amounts with cents. Defaults to `0`. */
  readonly decimals?: 0 | 2;
  readonly disabled?: boolean;
  readonly invalid?: boolean;
  readonly 'aria-label'?: string | undefined;
  readonly 'aria-describedby'?: string | undefined;
};

/**
 * Currency input with a `€` prefix, whole-Euro by default and taking cents with
 * `decimals={2}`. It masks the value with
 * `de-DE` thousands separators as the user types. The mask is presentation
 * only: `onChange` always emits a clean numeric value (or `null` when empty),
 * never the formatted string or the symbol. Parsing shares `@/lib/number-format`
 * with `NumberInput`. Visual parity with `PrefixInput`. Whole-Euro mode shows
 * „Nur ganze Euro“ below the field while the input holds a refused „,“ (IMM-1486).
 */
export function CurrencyInput(props: CurrencyInputProps) {
  const { id, value, placeholder, onChange, decimals = 0, disabled = false, invalid = false } = props;
  const ariaDescribedBy = props['aria-describedby'];
  const ariaLabel = props['aria-label'];

  const t = useTranslations('components.currency-input');
  const hintId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const caretDigitIndex = useRef<number | null>(null);
  const caretAfterSeparator = useRef(false);

  const [display, setDisplay] = useState(() => formatStoredAmount(value, decimals));
  const [lastEmitted, setLastEmitted] = useState<number | null>(value);
  const [wholeEuroHint, setWholeEuroHint] = useState(false);

  // Re-sync the display when the controlled value changes from the outside
  // (mount with a stored value, form reset) — but not when it merely echoes
  // back what this input just emitted, which would clobber the caret.
  if (value !== lastEmitted) {
    setLastEmitted(value);
    setDisplay(formatStoredAmount(value, decimals));
    setWholeEuroHint(false);
  }

  useLayoutEffect(() => {
    const target = caretDigitIndex.current;

    if (target === null || inputRef.current === null) return;

    const caret = caretAfterSeparator.current
      ? caretAfterDecimalSeparator(display, target)
      : caretForDigitIndex(display, target);
    inputRef.current.setSelectionRange(caret, caret);
    caretDigitIndex.current = null;
    caretAfterSeparator.current = false;
  }, [display]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const selectionStart = event.target.selectionStart ?? raw.length;

    caretDigitIndex.current = digitsBeforeCaret(raw, selectionStart);

    caretAfterSeparator.current = raw[selectionStart - 1] === ',';

    const formatted = formatDecimalInput(raw, decimals);

    setDisplay(formatted);
    setWholeEuroHint(decimals === 0 && formatted.includes(DECIMAL_SEPARATOR));

    const next = parseDecimalInput(raw, decimals);
    setLastEmitted(next);
    onChange(next);
  };

  const wrapperClasses = [
    'flex h-control-md w-full items-stretch overflow-hidden rounded-md border bg-surface',
    'transition-colors duration-micro ease-out',
    'focus-within:border-interactive focus-within:shadow-focus',
    invalid ? 'border-danger-500 focus-within:border-danger-500 focus-within:shadow-focus-danger' : 'border-border',
    disabled ? 'opacity-60' : '',
  ]
    .filter((c) => c !== '')
    .join(' ');

  const describedBy =
    [ariaDescribedBy, wholeEuroHint ? hintId : undefined].filter((v) => v !== undefined).join(' ') || undefined;

  return (
    <div className="w-full">
      <div className={wrapperClasses}>
        <span
          aria-hidden
          className="inline-flex items-center border-r border-border bg-neutral-50 px-3 text-base-sm text-text-tertiary"
        >
          {CURRENCY_SYMBOL}
        </span>
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={display}
          placeholder={placeholder}
          onChange={handleChange}
          onBlur={() => setDisplay(formatStoredAmount(value, decimals))}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-label={ariaLabel}
          aria-describedby={describedBy}
          inputMode={decimals > 0 ? 'decimal' : 'numeric'}
          className="min-w-0 flex-1 bg-transparent px-3 py-2 text-base font-sans text-text-primary focus:outline-none disabled:cursor-not-allowed"
        />
      </div>
      <div aria-live="polite">
        {wholeEuroHint && (
          <p id={hintId} className="mt-1 text-sm text-text-tertiary">
            {t('whole-euro-hint')}
          </p>
        )}
      </div>
    </div>
  );
}
